import React, { useState } from 'react';
import { FileText } from 'lucide-react';

interface DocumentInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

const DocumentInput: React.FC<DocumentInputProps> = ({
  value,
  onChange,
  placeholder = "Enter document ID",
  label = "Documento de Identificação",
  required = false,
  disabled = false,
  error
}) => {
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const validateDocument = (doc: string): { isValid: boolean; message: string } => {
    // Trim leading/trailing spaces for validation
    const trimmedDoc = doc.trim();
    
    if (!trimmedDoc) {
      return { isValid: !required, message: required ? 'Documento é obrigatório' : '' };
    }

    // Validação básica: pelo menos 5 caracteres (sem contar espaços nas pontas)
    if (trimmedDoc.length < 5) {
      return { isValid: false, message: 'Documento deve ter pelo menos 5 caracteres' };
    }

    // Validação: máximo 30 caracteres (sem contar espaços nas pontas)
    if (trimmedDoc.length > 30) {
      return { isValid: false, message: 'Documento muito longo (máximo 30 caracteres)' };
    }

    return { isValid: true, message: '' };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Permitir apenas letras, números, espaços e alguns caracteres especiais
    // Hyphen at end of character class for clarity (no escape needed)
    let sanitized = inputValue.replace(/[^a-zA-Z0-9\s.-]/g, '');
    
    // Collapse multiple spaces into single space
    sanitized = sanitized.replace(/\s+/g, ' ');
    
    onChange(sanitized);
    
    // Validar
    const validation = validateDocument(sanitized);
    setIsValid(validation.isValid);
    setErrorMessage(validation.message);
  };

  const handleBlur = () => {
    if (value) {
      // Trim leading/trailing spaces on blur
      const trimmedValue = value.trim();
      if (trimmedValue !== value) {
        onChange(trimmedValue);
      }
      
      const validation = validateDocument(trimmedValue);
      setIsValid(validation.isValid);
      setErrorMessage(validation.message);
    }
  };

  return (
    <div style={{ width: '100%', marginBottom: '16px' }}>
      {label && (
        <label style={{
          display: 'block',
          marginBottom: '5px',
          fontWeight: '500',
          fontSize: '14px',
          color: '#374151'
        }}>
          {label}
          {required && <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={30}
          style={{
            width: '100%',
            padding: '12px 40px 12px 12px',
            border: `2px solid ${error || !isValid ? '#dc2626' : '#d1d5db'}`,
            borderRadius: '6px',
            outline: 'none',
            fontSize: '14px',
            backgroundColor: disabled ? '#f9fafb' : 'white',
            transition: 'border-color 0.2s'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = error || !isValid ? '#dc2626' : '#3b82f6';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = error || !isValid ? '#dc2626' : '#d1d5db';
            handleBlur();
          }}
        />
        
        <div style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#6b7280',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <FileText size={18} />
        </div>
      </div>
      
      {(error || !isValid) && (
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: '#dc2626',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>⚠️</span>
          {error || errorMessage}
        </div>
      )}
      
      {isValid && value && value.length >= 5 && (
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: '#059669',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>✅</span>
          Documento válido
        </div>
      )}
      
      <div style={{
        marginTop: '4px',
        fontSize: '11px',
        color: '#6b7280'
      }}>
        💡 Aceita letras, números, espaços, hífens e pontos
      </div>
    </div>
  );
};

export default DocumentInput;

